import { useState } from 'react';
import { Check, Copy, Link2, Trash2 } from 'lucide-react';
import type { FileShare, JobFileEntry } from '@jobos/shared';
import { MAX_FILE_SHARE_LABEL } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  shareUrl,
  useCreateFileShare,
  useFileShares,
  useRevokeFileShare,
} from '@/hooks/useFileShares';

// Turn a selection into a link a client can open, and manage the links this job
// already has.
//
// One dialog rather than two: the question "is there already a link for this?"
// comes up at exactly the moment you are about to make another one, and the
// answer is worth having in front of you before you do.
//
// The link is not emailed. The office writes its own email — this only replaces
// the trip through Drive.

interface JobFileShareDialogProps {
  jobId: string;
  /** The files to share; null closes the dialog. */
  files: JobFileEntry[] | null;
  onClose: () => void;
}

export function JobFileShareDialog({ jobId, files, onClose }: JobFileShareDialogProps) {
  const open = files != null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {open && (
        <DialogContent className="max-w-lg">
          {/* Keyed on the selection so reopening on a different one starts with
              an empty label and no stale "here's your link" block. */}
          <ShareForm key={files.map((file) => file.id).join(',')} jobId={jobId} files={files} />
        </DialogContent>
      )}
    </Dialog>
  );
}

function ShareForm({ jobId, files }: { jobId: string; files: JobFileEntry[] }) {
  const [label, setLabel] = useState('');
  // The link made in this sitting. Kept separately from the list so it can sit
  // at the top with its copy button, which is what the user came here for.
  const [created, setCreated] = useState<FileShare | null>(null);

  const { data: shares, isLoading } = useFileShares(jobId);
  const { mutateAsync: createShare, isPending: creating } = useCreateFileShare(jobId);

  const handleCreate = async () => {
    const share = await createShare({
      fileIds: files.map((file) => file.id),
      ...(label.trim() ? { label: label.trim() } : {}),
    }).catch(() => null);

    if (share) setCreated(share);
  };

  // The link just made is already in the list once it refetches; showing it in
  // both places would read as two links.
  const others = (shares ?? []).filter((share) => share.id !== created?.id);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Share files</DialogTitle>
        <DialogDescription>
          {files.length === 1
            ? 'Creates a link to this file that anyone can open.'
            : `Creates a link to these ${files.length} files that anyone can open.`}
        </DialogDescription>
      </DialogHeader>

      {created ? (
        <CreatedLink share={created} />
      ) : (
        <div className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="share-label" className="text-sm font-medium text-gray-700">
              Label <span className="font-normal text-gray-500">(optional, for your reference)</span>
            </label>
            <Input
              id="share-label"
              value={label}
              maxLength={MAX_FILE_SHARE_LABEL}
              placeholder="Kitchen — before photos"
              onChange={(event) => setLabel(event.target.value)}
            />
            <p className="text-xs text-gray-500">The client never sees this.</p>
          </div>

          <Button onClick={handleCreate} disabled={creating} className="w-full">
            <Link2 className="mr-2 h-4 w-4" />
            {creating ? 'Creating link…' : 'Create link'}
          </Button>
        </div>
      )}

      <div className="space-y-2 border-t pt-3">
        <h3 className="text-sm font-medium text-gray-700">Other links on this job</h3>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : others.length === 0 ? (
          <p className="text-sm text-gray-500">None.</p>
        ) : (
          <ul className="max-h-56 space-y-2 overflow-y-auto">
            {others.map((share) => (
              <ShareRow key={share.id} jobId={jobId} share={share} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/** The link just created, with the copy button that is the point of the dialog. */
function CreatedLink({ share }: { share: FileShare }) {
  return (
    <div className="space-y-2 rounded border border-green-200 bg-green-50 p-3">
      <p className="text-sm font-medium text-green-900">
        Link created — {share.fileCount} {share.fileCount === 1 ? 'file' : 'files'}
      </p>
      <div className="flex items-center gap-2">
        <Input readOnly value={shareUrl(share.token)} className="bg-white text-xs" />
        <CopyLinkButton token={share.token} />
      </div>
      <p className="text-xs text-green-900">
        Anyone with the link can open it until you revoke it.
      </p>
    </div>
  );
}

function CopyLinkButton({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl(token));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused outside a secure context, and a phone on the
      // LAN is exactly that case. Say so rather than failing silently — the URL
      // is on screen and can be selected by hand.
      toast({
        title: "Couldn't copy",
        description: 'Select the link and copy it manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button size="sm" variant="outline" onClick={handleCopy} aria-label="Copy link">
      {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

function ShareRow({ jobId, share }: { jobId: string; share: FileShare }) {
  const { mutate: revoke, isPending } = useRevokeFileShare(jobId);

  return (
    <li className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">
          {share.label ?? 'Untitled link'}
        </p>
        <p className="text-xs text-gray-500">
          {share.fileCount} {share.fileCount === 1 ? 'file' : 'files'} ·{' '}
          {new Date(share.createdAt).toLocaleDateString()}
          {share.createdByName ? ` · ${share.createdByName}` : ''}
        </p>
      </div>
      <CopyLinkButton token={share.token} />
      <Button
        size="sm"
        variant="outline"
        disabled={isPending}
        onClick={() => revoke(share.id)}
        aria-label={`Revoke ${share.label ?? 'link'}`}
      >
        <Trash2 className="h-4 w-4 text-red-600" />
      </Button>
    </li>
  );
}
